/**
 * Gemeinsame Bausteine der Willkommensseite.
 *
 * Enthält Logo und Mitmachen_Aktion (In-App-Link zum Registrierungs_Ziel) —
 * die einzigen Stellen, die CTA-Ziele kennen. Alle Texte kommen aus dem
 * i18n-Katalog (`welcome.*`).
 */

import type { MouseEvent, ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useI18n } from "../../i18n/I18nProvider";

// ---------------------------------------------------------------------------
// Konstanten
// ---------------------------------------------------------------------------

/** Ziel der Mitmachen_Aktion: Index-Route im Registrieren-Modus (Req 7.2). */
export const REGISTER_TARGET = "/?mode=register";

// ---------------------------------------------------------------------------
// Sanftes Anker-Scrollen
// ---------------------------------------------------------------------------

/**
 * Sanftes Scrollen mit eigener Animation: `scrollIntoView({ behavior:
 * "smooth" })` nutzt eine browserfeste (kurze) Dauer; mit requestAnimationFrame
 * steuern wir Dauer und Easing selbst. Läuft asynchron neben dem Rendern.
 */

/** ID der laufenden Scroll-Animation (max. eine gleichzeitig). */
let activeScrollAnimation: number | null = null;

/** Beendet eine laufende Scroll-Animation und ihre Abbruch-Listener. */
function cancelScrollAnimation() {
  if (activeScrollAnimation !== null) {
    cancelAnimationFrame(activeScrollAnimation);
    activeScrollAnimation = null;
  }
  window.removeEventListener("wheel", cancelScrollAnimation);
  window.removeEventListener("touchstart", cancelScrollAnimation);
  window.removeEventListener("keydown", cancelScrollAnimation);
}

/** Sanftes Ease-in-out: langsam starten, mittig schnell, weich auslaufen. */
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Animiert das Fenster von der aktuellen Position zu `targetY`. Die Dauer
 * skaliert mit der Distanz (700–1500 ms), damit lange Strecken nicht hetzen
 * und kurze nicht schleppen. Ein eigener Scroll-Impuls des Nutzers (Rad,
 * Touch, Tastatur) bricht die Animation sofort ab.
 */
function animateScrollTo(targetY: number) {
  cancelScrollAnimation();

  const startY = window.scrollY;
  const distance = targetY - startY;
  if (distance === 0) return;

  const duration = Math.min(1500, Math.max(700, Math.abs(distance) * 0.6));
  const startTime = performance.now();

  const step = (now: number) => {
    const progress = Math.min((now - startTime) / duration, 1);
    window.scrollTo(0, startY + distance * easeInOutCubic(progress));
    if (progress < 1) {
      activeScrollAnimation = requestAnimationFrame(step);
    } else {
      cancelScrollAnimation();
    }
  };

  // Nutzer-Eingriff gewinnt immer gegen die Animation.
  window.addEventListener("wheel", cancelScrollAnimation, { passive: true });
  window.addEventListener("touchstart", cancelScrollAnimation, { passive: true });
  window.addEventListener("keydown", cancelScrollAnimation);

  activeScrollAnimation = requestAnimationFrame(step);
}

/**
 * Klick-Handler für Anker-Links (`href="#…"`) der Willkommensseite: scrollt
 * sanft zum Zielabschnitt, ohne den Browser-Sprung. Das `href`-Attribut bleibt
 * erhalten (Semantik, Tastatur, „Link kopieren"); nur das Standard-Sprung-
 * Verhalten wird ersetzt. Existiert das Ziel nicht, greift das normale
 * Browser-Verhalten. `prefers-reduced-motion` scrollt ohne Animation.
 */
export function scrollToAnchor(event: MouseEvent<HTMLAnchorElement>) {
  const href = event.currentTarget.getAttribute("href");
  if (!href || !href.startsWith("#")) return;
  const target = document.getElementById(href.slice(1));
  if (!target) return; // kein Ziel → Browser-Standard
  event.preventDefault();

  // scroll-margin-top des Ziels respektieren (hält unter dem sticky Header).
  const styles = window.getComputedStyle(target);
  const scrollMarginTop = parseFloat(styles.scrollMarginTop) || 0;
  const targetY = Math.max(
    0,
    target.getBoundingClientRect().top + window.scrollY - scrollMarginTop,
  );

  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduceMotion) {
    window.scrollTo(0, targetY);
  } else {
    animateScrollTo(targetY);
  }

  // Hash setzen, ohne sofortigen Sprung (replaceState statt location.hash).
  window.history.replaceState(null, "", href);
}

// ---------------------------------------------------------------------------
// Logo
// ---------------------------------------------------------------------------

/**
 * Logo der Willkommensseite als statische Datei aus `public` (Req 2.4); der
 * Alternativtext kommt aus dem i18n-Katalog (Req 2.3). Über `src`/`srcDark`
 * kann je Seitenbereich ein anderes Logo-Asset gesetzt werden; im Dunkelmodus
 * greift `srcDark`, falls vorhanden (Muster aus `AppHead`).
 */
export function WelcomeLogo({
  width = 160,
  src = "/se-logo-welcome-hell.png",
  srcDark = "/se-logo-welcome-dunkel.png",
}: {
  width?: number | { xs?: number; sm?: number; md?: number };
  src?: string;
  srcDark?: string;
}) {
  const { t } = useI18n();
  const theme = useTheme();
  const resolvedSrc =
    theme.palette.mode === "dark" && srcDark != null ? srcDark : src;
  return (
    <Box
      component="img"
      src={resolvedSrc}
      alt={t("welcome.logo.alt")}
      sx={{
        display: "block",
        width,
        maxWidth: "100%",
        height: "auto",
        objectFit: "contain",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Mitmachen_Aktion
// ---------------------------------------------------------------------------

/**
 * Primärer Handlungsaufruf: navigiert in-app (SPA, kein Full-Reload, Req 7.3)
 * zum Registrierungs_Ziel `/?mode=register` (Req 7.2).
 */
export function JoinButton({
  labelKey = "welcome.hero.join",
  variant = "contained",
}: {
  labelKey?: string;
  variant?: "contained" | "outlined";
}) {
  const { t } = useI18n();
  return (
    <Button
      component={RouterLink}
      to={REGISTER_TARGET}
      variant={variant}
      size="large"
      endIcon={<ArrowForwardRoundedIcon />}
      sx={{ minHeight: 48 }}
    >
      {t(labelKey)}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Abschnitts-Etikett
// ---------------------------------------------------------------------------

/** Kleines, farbiges Etikett über Abschnittstiteln (rein dekorativ). */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        color: "secondary.main",
        fontWeight: 800,
        fontSize: 13,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}
